(() => {
  const root = document.querySelector('[data-tss-checkout][data-tss-cart-source="shopify"]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';
  const q = name => root.querySelector(`[data-tss-${name}]`);
  const dialog = q('dialog');
  const status = q('status');
  const checkoutLink = q('checkout-link');
  const shell = q('checkout-shell');
  const frame = q('checkout-frame');
  const api = root.dataset.apiBaseUrl.replace(/\/$/, '');
  const apiOrigin = new URL(api).origin;
  const cartRoot = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  const route = name => `${cartRoot}/${name}`;
  let cart = null;
  let session = null;
  let busy = false;
  let pendingRequest = null;
  let retryCheckout = false;
  let shellTimer;
  let checkoutReady = false;
  let exitRecoveryReady = false;
  let exitRequestTimer;
  let exitBackPending = false;
  function requestCheckoutExit(back = true) {
    if (!exitRecoveryReady || shell.dataset.gw === 'true' || !checkoutReady) return closeCheckout(back);
    if (exitRequestTimer) return;
    exitBackPending = !back;
    frame.contentWindow?.postMessage({ source: 'tss-storefront', type: 'request-exit' }, apiOrigin);
    exitRequestTimer = setTimeout(() => { exitRequestTimer = null; closeCheckout(back); }, 2000);
  }
  let restoreFocus;
  let viewVersion = 0;
  let purchasedLines = [];
  let offerSeen = false;
  let sessionSig = '';
  let sessionAt = 0;
  let tail = Promise.resolve();
  let working = 0;
  const pending = new Map();
  let flushTimer = 0;
  let lastRemoved = null;
  let undoTimer = 0;
  const PRICED_KEY = 'tss_priced_v1';
  const PRICED_MAX_AGE = 120000;
  const MAX_QUANTITY = 10;
  const MAX_LINES = 20;
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: currency || 'INR' }).format(Number(amount));
  const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'campaign_id', 'ad_id', 'adset_id', 'fbclid', 'gclid', 'gbraid', 'wbraid'];
  const params = new URLSearchParams(location.search);
  let attribution = {};
  try { attribution = JSON.parse(sessionStorage.getItem('tss_attribution_v1') || '{}'); } catch {}
  if (!Number.isFinite(attribution.capturedAt) || Date.now() - attribution.capturedAt > 1800000 || fields.some(key => params.has(key))) {
    attribution = { capturedAt: Date.now(), landing_page_url: location.href, referrer_url: document.referrer };
    fields.forEach(key => { if (params.get(key)) attribution[key] = params.get(key); });
    try { sessionStorage.setItem('tss_attribution_v1', JSON.stringify(attribution)); } catch {}
  }
  let reference;
  try { reference = localStorage.getItem('tss_cart_reference_v1'); } catch {}
  if (!reference) { reference = crypto.randomUUID(); try { localStorage.setItem('tss_cart_reference_v1', reference); } catch {} }
  const cookie = name => { try { return decodeURIComponent(document.cookie.split('; ').find(part => part.startsWith(`${name}=`))?.slice(name.length + 1) || ''); } catch { return ''; } };
  const tracking = () => {
    const value = {};
    const fbp = cookie('_fbp'), fbc = cookie('_fbc');
    if (fbp) value.fbp = fbp;
    if (attribution.fbclid && !fbc.endsWith(`.${attribution.fbclid}`)) value.fbc = `fb.1.${attribution.capturedAt}.${attribution.fbclid}`;
    else if (fbc) value.fbc = fbc;
    const ga = cookie('_ga').match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
    if (ga) value.gaClientId = ga[1];
    return value;
  };
  const properties = value => Object.fromEntries(Object.entries(value || {}).filter(([, v]) => typeof v === 'string' && v.trim()));
  const gid = value => {
    const id = String(value || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    if (!/^\d+$/.test(id)) throw new Error(root.dataset.errorLabel);
    return `gid://shopify/ProductVariant/${id}`;
  };
  async function request(url, options = {}) {
    const res = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers }, signal: AbortSignal.timeout(15000) });
    const value = await res.json();
    if (!res.ok) throw new Error(url.startsWith(api) ? root.dataset.unavailableLabel : value.description || root.dataset.errorLabel);
    return value;
  }
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  // Shopify answers 409 when cart writes collide; the writes here are queued one at a time, so a short retry is enough.
  async function cartWrite(name, body) {
    for (let attempt = 0; ; attempt += 1) {
      const res = await fetch(route(name), { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(15000) });
      if (res.status === 409 && attempt < 2) { await sleep(250 * (attempt + 1)); continue; }
      const value = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(value.description || value.message || root.dataset.errorLabel);
      return value;
    }
  }
  function setBusy(on) { busy = on; q('lines').setAttribute('aria-busy', String(on)); }
  // Everything that talks to the cart runs here, one at a time and in order (adds, quantity batches, opening and checkout).
  const enqueue = fn => {
    working += 1; setBusy(true);
    const result = tail.then(fn);
    tail = result.then(() => {}, () => {});
    return result.finally(() => { working -= 1; if (!working) { setBusy(false); if (!session && cart?.items?.length) renderBookOffer(); } });
  };
  const open = () => {
    if (dialog.open) return;
    restoreFocus = document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('tss-cart-open');
    (q('scroll-body') || q('lines')).scrollTop = 0;
  };
  // Instant feedback: the tapped button shows a spinner and "Adding…" at once (and cannot be tapped twice), and the drawer shows the book as
  // a pending row, before Shopify has answered. Both are replaced by the real thing; on a failure the row goes and the button comes back.
  function markPending(button, label) {
    if (!button || button.dataset.tssPending) return () => {};
    const html = button.innerHTML, wasDisabled = button.disabled;
    button.dataset.tssPending = 'true'; button.setAttribute('aria-busy', 'true'); button.classList.add('tss-adding');
    const spinner = document.createElement('span'); spinner.className = 'tss-spinner'; spinner.setAttribute('aria-hidden', 'true');
    button.replaceChildren(spinner, document.createTextNode(' ' + label)); button.disabled = true;
    return () => { button.innerHTML = html; button.disabled = wasDisabled; button.removeAttribute('aria-busy'); button.classList.remove('tss-adding'); delete button.dataset.tssPending; };
  }
  function showPendingRow(preview) {
    const row = q('line-template').content.cloneNode(true);
    const get = name => row.querySelector('[data-tss-' + name + ']');
    row.firstElementChild.classList.add('tss-checkout__line--pending');
    get('title').textContent = preview?.title || 'Adding to your cart…';
    get('variant').textContent = ''; get('price').textContent = '…';
    if (preview?.image) { get('image').src = preview.image; get('image').alt = ''; } else get('image').hidden = true;
    row.querySelectorAll('button, select').forEach(node => { node.disabled = true; });
    q('lines').prepend(row); (q('scroll-body') || q('lines')).scrollTop = 0;
  }
  // What the page already knows about the product being added: its name and picture.
  function pagePreview(form) {
    const scope = form?.closest('product-info, .product, section') || document;
    // The page's heading can hold hidden copies of the name, so the clean og:title comes first, then the heading's first line.
    const heading = (scope.querySelector('h1, .product__title')?.textContent || document.querySelector('h1')?.textContent || '').split(/\r?\n/).map(part => part.trim()).find(Boolean) || '';
    const title = (document.querySelector('meta[property="og:title"]')?.content || heading).trim().slice(0, 120);
    const image = (document.querySelector('meta[property="og:image"]')?.content || scope.querySelector('img')?.currentSrc || '').replace(/^http:/, 'https:');
    return { title, image };
  }
  function countHeader() {
    const n = cart?.item_count || 0;
    if (q('cart-count')) q('cart-count').textContent = `${n} ${n === 1 ? 'item' : 'items'}`;
    document.querySelectorAll('#cart-icon-bubble, header a[href$="/cart"]').forEach(icon => {
      icon.querySelector('.cart-count-bubble')?.remove();
      if (n) {
        const badge = document.createElement('div'); badge.className = 'cart-count-bubble';
        const visible = document.createElement('span'); visible.textContent = String(n); visible.setAttribute('aria-hidden', 'true');
        const accessible = document.createElement('span'); accessible.className = 'visually-hidden'; accessible.textContent = n + ' ' + root.dataset.itemsLabel;
        badge.append(visible, accessible); icon.append(badge);
      }
    });
  }
  // The cart's lines in the form our server prices, and a signature that says whether a priced result still belongs to a cart.
  const lineOf = item => ({ merchandiseId: gid(item.variant_id), quantity: item.quantity, ...(Object.keys(properties(item.properties)).length ? { properties: properties(item.properties) } : {}) });
  const canon = lines => JSON.stringify(lines.map(line => [line.merchandiseId, line.quantity, Object.entries(line.properties || {}).sort(([a], [b]) => (a < b ? -1 : 1))]));
  const signature = (lines, token) => token + '|' + canon(lines);
  const withinLimits = lines => lines.length <= MAX_LINES && lines.every(line => line.quantity <= MAX_QUANTITY);
  // The last priced cart is kept for two minutes (our server keeps a session for thirty), so another page opens with the right numbers at once.
  function readPriced(sig) {
    try { const value = JSON.parse(sessionStorage.getItem(PRICED_KEY) || 'null'); if (value && value.sig === sig && Date.now() - value.at < PRICED_MAX_AGE && value.session?.id) return value; } catch {}
    return null;
  }
  function writePriced(sig, value) { try { sessionStorage.setItem(PRICED_KEY, JSON.stringify({ sig, at: Date.now(), session: value })); } catch {} }
  // Keeps the priced result only while it still matches the cart on screen; otherwise looks for a cached one.
  function syncSession() {
    let sig = '';
    try { sig = cart?.items?.length ? signature(cart.items.map(lineOf), cart.token) : ''; } catch { sig = ''; }
    if (!sig) { session = null; sessionSig = ''; return; }
    if (session && sessionSig === sig) return;
    const hit = readPriced(sig);
    session = hit ? hit.session : null; sessionSig = hit ? sig : ''; sessionAt = hit ? hit.at : 0;
  }
  function focusedControl() {
    const el = document.activeElement;
    if (!el || !q('lines').contains(el)) return null;
    const name = ['up', 'down', 'remove', 'quantity'].find(part => el.hasAttribute('data-tss-' + part));
    const key = el.closest('[data-key]')?.dataset.key;
    return name && key ? { key, name } : null;
  }
  function refocus(info) {
    if (!info) return;
    const target = [...q('lines').querySelectorAll('[data-key]')].find(row => row.dataset.key === info.key)?.querySelector('[data-tss-' + info.name + ']');
    if (target && !target.disabled) target.focus({ preventScroll: true });
  }
  function render() {
    const scroller = q('scroll-body') || q('lines');
    const scrollTop = scroller.scrollTop;
    const expanded = new Set([...root.querySelectorAll('[data-tss-line-key] details[open]')].map(node => node.closest('[data-tss-line-key]').dataset.tssLineKey));
    const focused = document.activeElement;
    const focusedKey = focused?.closest('[data-tss-line-key]')?.dataset.tssLineKey;
    const focusedControl = focusedKey ? [...focused.attributes].find(attr => /^data-tss-(up|down|quantity|remove)$/.test(attr.name))?.name : null;
    q('lines').replaceChildren();
    countHeader();
    (cart?.items || []).forEach((item, index) => {
      const row = q('line-template').content.cloneNode(true);
      const get = name => row.querySelector(`[data-tss-${name}]`);
      row.firstElementChild.dataset.tssLineKey = item.key; row.firstElementChild.dataset.key = item.key;
      const allocated = session?.lines?.filter((line, position) => (line.sourceLineIndex ?? position) === index) || [];
      const validated = allocated[0];
      get('title').textContent = validated?.title || item.product_title || item.title;
      get('variant').textContent = item.variant_title === 'Default Title' ? '' : (item.variant_title || '').split(' / ').filter(option => !/^(skip candle|no extras)$/i.test(option)).map(option => option.replace(/Standard\s*\(A4\)/i, 'A4').replace(/Jumbo\s*\(A3\)/i, 'A3').replace(/^Book Only$/i, 'Book only')).join(' · ');
      const lineTotal = validated ? allocated.reduce((sum, line) => sum + Math.round(Number(line.unitPrice) * 100) * line.quantity, 0) : item.final_line_price;
      get('price').textContent = session ? money(lineTotal / 100, cart.currency) : '…';
      get('price').classList.toggle('tss-checkout__price--pending', !session);
      const originalTotal = validated ? allocated.reduce((sum, line) => sum + Math.round(Number(line.originalUnitPrice || line.unitPrice) * 100) * line.quantity, 0) : lineTotal;
      if (get('original-price')) { get('original-price').hidden = !session || originalTotal <= lineTotal; get('original-price').textContent = money(originalTotal / 100, cart.currency); }
      const image = validated?.image?.url || item.featured_image?.url || item.image;
      if (image) { get('image').src = image; get('image').alt = item.product_title || item.title; get('image').addEventListener('error', () => { get('image').hidden = true; if (get('image-fallback')) get('image-fallback').hidden = false; }); } else { get('image').hidden = true; if (get('image-fallback')) get('image-fallback').hidden = false; }
      const choices = document.createElement('p'); choices.className = 'tss-checkout__choices';
      choices.textContent = Object.entries(properties(item.properties)).filter(([key]) => !key.startsWith('_')).map(([key, value]) => `${key}: ${value}`).join('\n');
      if (choices.textContent) {
        const details = document.createElement('details'); details.className = 'tss-checkout__choices-details'; details.open = expanded.has(item.key);
        const summary = document.createElement('summary'); summary.textContent = root.dataset.choicesLabel || 'View selected books';
        details.append(summary, choices); get('variant').after(details);
      }
      get('quantity').value = String(item.quantity);
      get('quantity').setAttribute('aria-label', root.dataset.quantityLabel + ': ' + (item.product_title || item.title));
      get('quantity').addEventListener('change', event => { if (event.target.value !== '') setQuantity(item.key, Number(event.target.value)); });
      get('down').disabled = item.quantity <= 1;
      get('down').addEventListener('click', () => setQuantity(item.key, item.quantity - 1));
      get('up').disabled = item.quantity >= MAX_QUANTITY;
      get('up').addEventListener('click', () => setQuantity(item.key, item.quantity + 1));
      get('remove').addEventListener('click', () => setQuantity(item.key, 0));
      q('lines').append(row);
    });
    q('total').textContent = session ? money(session.subtotal, session.currencyCode) : cart?.item_count ? '…' : '—';
    q('total').classList.toggle('tss-checkout__total--pending', !session && Boolean(cart?.item_count));
    renderBookOffer();
    scroller.scrollTop = scrollTop;
    if (focusedKey && focusedControl) [...q('lines').querySelectorAll('[data-tss-line-key]')].find(node => node.dataset.tssLineKey === focusedKey)?.querySelector(`[${focusedControl}]`)?.focus({ preventScroll: true });
    if (!cart?.items?.length) status.textContent = root.dataset.emptyLabel;
    if (session && cart?.items?.length) { checkoutLink.setAttribute('aria-disabled', 'false'); checkoutLink.href = hostedUrl(session); }
    else { checkoutLink.setAttribute('aria-disabled', 'true'); checkoutLink.removeAttribute('href'); }
  }
  function renderBookOffer() {
    const offer = session?.automaticBookOffer;
    const show = offer?.version === 1 && Number.isInteger(offer.eligibleQuantity) && offer.eligibleQuantity > 0;
    if (!q('book-offer')) return;
    if (show) offerSeen = true;
    // While the price is on its way, keep the offer panel in place (same height) so nothing jumps when it arrives. Not for an empty cart.
    const active = busy || pending.size > 0 || Boolean(flushTimer);
    const waiting = !show && !session && Boolean(cart?.items?.length) && active && (offerSeen || cart.items.some(item => item.original_price === 89900));
    q('book-offer').hidden = !show && !waiting;
    q('book-offer').classList.toggle('is-loading', waiting);
    q('offer-savings-row').hidden = !show || Number(offer.savings) <= 0;
    if (waiting) { q('offer-message').textContent = 'Checking your book offer…'; q('offer-fill').style.width = '0%'; q('offer-progress').setAttribute('aria-valuenow', '0'); root.querySelectorAll('[data-tss-offer-tier]').forEach(node => { node.dataset.unlocked = 'false'; }); }
    if (!show) { if (q('offer-detail')) q('offer-detail').textContent = ''; if (q('offer-success')) q('offer-success').hidden = true; q('offer-progress').hidden = false; q('book-offer').dataset.complete = 'false'; return; }
    const count = offer.eligibleQuantity;
    const fallback = count === 1 ? ['Get 2 books for ₹1,399', 'Add one more book and save ₹399.'] : count === 2 ? ['You saved ₹399!', 'Add a third book for just ₹400 more—3 for ₹1,799.'] : count === 3 ? ['You saved ₹898!', 'Add a fourth book for just ₹400 more—4 for ₹2,199.'] : ['Your 4-book offer is applied!', count > 4 ? 'You saved ₹1,397. Additional books are charged at their regular price.' : 'You saved ₹1,397.'];
    q('book-offer').dataset.complete = String(count >= 4);
    q('offer-message').textContent = q('offer-detail') ? offer.headline || fallback[0] : offer.message;
    if (q('offer-detail')) q('offer-detail').textContent = offer.detail || fallback[1];
    if (q('offer-success')) q('offer-success').hidden = count < 4;
    q('offer-progress').hidden = count >= 4;
    q('offer-progress').setAttribute('aria-valuenow', String(Math.min(4, offer.eligibleQuantity)));
    q('offer-progress').setAttribute('aria-valuetext', `${offer.eligibleQuantity} eligible books in your cart`);
    q('offer-fill').style.width = `${Math.min(4, offer.eligibleQuantity) * 25}%`;
    root.querySelectorAll('[data-tss-offer-tier]').forEach(node => { node.dataset.unlocked = String(offer.eligibleQuantity >= Number(node.dataset.tssOfferTier)); });
    q('offer-savings').textContent = `−${money(offer.savings, session.currencyCode)}`;
    if (offer.eligibleQuantity >= 4) return;
    let catalog = [];
    try { catalog = JSON.parse(q('offer-catalog')?.textContent || '[]'); } catch {}
    const inCart = new Set(cart.items.map(item => String(item.variant_id)));
    const productsInCart = new Set(cart.items.filter(item => item.product_id).map(item => String(item.product_id)));
    const handlesInCart = new Set(cart.items.flatMap(item => (item.properties?._bundle_handles || '').split(/[\s,|]+/).filter(Boolean)));
    const titlesInCart = new Set(cart.items.map(item => (item.product_title || item.title || '').toLowerCase()));
    const bundleTitles = cart.items.map(item => item.properties?.Books || '').join('\n').toLowerCase();
    const suggestions = catalog.filter(book => /^\d+$/.test(String(book.id)) && book.price === 899 && !inCart.has(String(book.id)) && !productsInCart.has(String(book.productId)) && !handlesInCart.has(book.handle) && !titlesInCart.has(book.title.toLowerCase()) && !bundleTitles.includes(book.title.toLowerCase())).slice(0, 3);
    if (!suggestions.length) return;
    const section = document.createElement('section'); section.className = 'tss-book-offer__recommendations'; section.setAttribute('aria-label', 'Add another eligible book');
    const heading = document.createElement('h3'); heading.textContent = root.dataset.recommendationsLabel || 'Choose your next book'; section.append(heading);
    const cards = document.createElement('div'); cards.className = 'tss-book-offer__cards'; section.append(cards);
    suggestions.forEach(book => {
      const row = document.createElement('article'); row.className = 'tss-book-offer__recommendation';
      if (book.image) { const img = document.createElement('img'); img.src = book.image; img.alt = book.title; img.width = 56; img.height = 64; img.loading = 'lazy'; row.append(img); }
      const detail = document.createElement('div'); const title = document.createElement('strong'); title.textContent = book.title; const note = document.createElement('small'); note.textContent = root.dataset.bookOptionLabel || 'A4 · Book only'; detail.append(title, note); row.append(detail);
      const button = document.createElement('button'); button.type = 'button'; button.textContent = root.dataset.bookAddLabel || 'Add book'; button.setAttribute('aria-label', `Add ${book.title}`);
      button.addEventListener('click', () => { addLines([{ id: book.id, quantity: 1 }], { preview: { title: book.title, image: book.image }, button }).catch(error => { status.textContent = error.message || root.dataset.errorLabel; }); });
      row.append(button); cards.append(row);
    });
    q('lines').append(section);
  }
  const hostedUrl = (value, embedded = false) => {
    const url = new URL(`${api}/checkout`); url.searchParams.set('session', value.id);
    if (embedded) url.searchParams.set('parentOrigin', location.origin);
    return url.href;
  };
  function closeCheckout(back = true) {
    clearTimeout(exitRequestTimer); exitRequestTimer = null; exitRecoveryReady = false; exitBackPending = false;
    clearTimeout(shellTimer);
    checkoutReady = false;
    shell.hidden = true; frame.hidden = true; frame.removeAttribute('src');
    document.documentElement.classList.remove('tss-checkout-open');
    if (back && history.state?.tssCheckout) history.back();
    restoreFocus?.focus?.();
  }
  function launch() {
    if (!session) return;
    clearTimeout(shellTimer);
    checkoutReady = false;
    exitRecoveryReady = false; exitBackPending = false;
    dialog.close(); shell.hidden = false; frame.hidden = true; q('checkout-loader').hidden = false; q('checkout-problem').hidden = true;
    document.documentElement.classList.add('tss-checkout-open');
    history.pushState({ tssCheckout: true }, '', location.href);
    purchasedLines = cart.items.map(item => ({ key: item.key, quantity: item.quantity }));
    try { sessionStorage.setItem('tss_native_checkout_lines_v1', JSON.stringify(purchasedLines)); } catch {}
    shellTimer = setTimeout(() => {
      if (checkoutReady || shell.hidden) return;
      q('checkout-loader').hidden = true;
      q('checkout-problem').hidden = false;
    }, 30000);
    frame.src = hostedUrl(session, true); q('checkout-close').focus();
  }
  // Asks our server to price these cart lines. A repeat of the same lines reuses its idempotency key, so a retry never creates a second session.
  function priceRequest(lines, token) {
    const sig = signature(lines, token);
    if (!pendingRequest || pendingRequest.signature !== sig || Date.now() - pendingRequest.createdAt > 1200000) {
      pendingRequest = { signature: sig, createdAt: Date.now(), key: crypto.randomUUID(), body: { shop: root.dataset.shopDomain, lines, attribution: { ...attribution, tss_cart_reference: reference, shopifyCartToken: token, tracking: tracking() } } };
    }
    return request(api + '/v1/checkout-sessions', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': pendingRequest.key }, body: JSON.stringify(pendingRequest.body) });
  }
  function checkPriced(result, lines) {
    if (result.status !== 'ready_for_shopify_checkout' || !result.id || !result.currencyCode || !Array.isArray(result.lines) || !result.lines.length) throw new Error(root.dataset.unavailableLabel);
    const sourceCounts = lines.map(() => 0);
    result.lines.forEach((saved, position) => {
      const index = saved.sourceLineIndex ?? (result.lines.length === lines.length ? position : -1);
      if (!Number.isInteger(index) || index < 0 || index >= lines.length || !Number.isInteger(saved.quantity) || saved.quantity < 1 || !Number.isFinite(Number(saved.unitPrice)) || Number(saved.unitPrice) < 0) throw new Error(root.dataset.metadataLabel);
      const line = lines[index];
      const actual = Object.fromEntries((saved.properties || []).map(property => [property.name, property.value]));
      if (saved.merchandiseId !== line.merchandiseId || Object.entries(line.properties || {}).some(([name, value]) => actual[name] !== value.trim())) throw new Error(root.dataset.metadataLabel);
      sourceCounts[index] += saved.quantity;
    });
    lines.forEach((line, index) => {
      if (sourceCounts[index] !== line.quantity) throw new Error(root.dataset.metadataLabel);
    });
  }
  // Prices the cart on screen. Skipped when it was priced moments ago (same lines, same cart); a price call already started for exactly these lines is reused.
  async function priceCurrent(early = null, earlySig = '') {
    const items = cart?.items || [];
    if (!items.length) { session = null; sessionSig = ''; return; }
    const lines = items.map(lineOf);
    if (!withinLimits(lines)) throw new Error(root.dataset.tooManyLabel);
    const sig = signature(lines, cart.token);
    if (session && sessionSig === sig && Date.now() - sessionAt < PRICED_MAX_AGE) return;
    const hit = readPriced(sig);
    if (hit) { session = hit.session; sessionSig = sig; sessionAt = hit.at; return; }
    const result = await (early && earlySig === sig ? early : priceRequest(lines, cart.token));
    checkPriced(result, lines);
    pendingRequest = null; writePriced(sig, result);
    // The cart may have been edited while this price was on its way: then it belongs to a cart that is no longer on screen.
    let now = '';
    try { now = signature((cart?.items || []).map(lineOf), cart?.token); } catch {}
    if (now !== sig) return;
    session = result; sessionSig = sig; sessionAt = Date.now();
  }
  // Shows a cart we already know (the answer to add or update) at once.
  function show(next) {
    const items = next.items || [];
    cart = { ...next, item_count: items.reduce((sum, item) => sum + item.quantity, 0), total_price: items.reduce((sum, item) => sum + item.final_line_price, 0) };
    syncSession(); render();
  }
  // Shopify's add answers with the full lines it added (a re-added line carries its new total). New lines go to the top and an existing line keeps its place.
  function showAdded(added) {
    // A visitor's first add makes a new cart with a new token. Shopify sets it in the "cart" cookie, which is the token cart.js reports.
    const token = cookie('cart') || cart?.token;
    if (!cart?.items || !token || !Array.isArray(added) || !added.length) return false;
    const items = [...cart.items];
    const fresh = [];
    added.forEach(item => { const at = items.findIndex(existing => existing.key === item.key); if (at >= 0) items[at] = item; else fresh.unshift(item); });
    show({ ...cart, token, items: [...fresh, ...items] });
    return true;
  }
  async function refresh(validate = true) {
    const fresh = await request(route('cart.js'));
    if (pending.size || flushTimer) return; // an edit is waiting to be sent: its own answer will bring the cart up to date
    cart = fresh;
    syncSession(); render();
    if (!validate) return;
    await priceCurrent();
    status.textContent = ''; render();
  }
  // A failed price leaves the cart on screen without prices, a reason, and a retry button.
  async function settle() {
    try { await priceCurrent(); status.textContent = ''; } catch (error) { session = null; status.textContent = error.message || root.dataset.errorLabel; q('retry').hidden = false; }
    render();
  }
  async function runNow(checkout) {
    const version = ++viewVersion;
    retryCheckout = checkout; q('retry').hidden = true;
    status.textContent = checkout ? root.dataset.loadingLabel : session ? '' : root.dataset.updatingLabel;
    try {
      await refresh(true);
      if (checkout && version === viewVersion) launch();
      return true;
    } catch (error) {
      session = null; render(); status.textContent = error.message || root.dataset.errorLabel; q('retry').hidden = false;
      return false;
    }
  }
  const run = (checkout = false) => { open(); if (pending.size) { clearTimeout(flushTimer); flushSoon(); } return enqueue(() => runNow(checkout)); };
  // ---- quantity: the screen changes at once, taps are batched into one Shopify call, and the answer is priced ----
  function setQuantity(key, quantity) {
    const item = cart?.items?.find(entry => entry.key === key);
    if (!Number.isFinite(Number(quantity))) return;
    const wanted = Math.max(0, Math.min(MAX_QUANTITY, Math.floor(Number(quantity))));
    if (!item || wanted === item.quantity) return;
    clearUndo(); lastRemoved = wanted === 0 ? { id: item.variant_id, quantity: item.quantity, properties: properties(item.properties), title: item.product_title || item.title } : null;
    pending.set(key, wanted);
    const items = cart.items.map(entry => (entry.key === key ? { ...entry, quantity: wanted, final_line_price: entry.final_price * wanted } : entry)).filter(entry => entry.quantity > 0);
    cart = { ...cart, items, item_count: items.reduce((sum, entry) => sum + entry.quantity, 0), total_price: items.reduce((sum, entry) => sum + entry.final_line_price, 0) };
    syncSession(); q('retry').hidden = true; status.textContent = ''; render();
    clearTimeout(flushTimer); flushTimer = setTimeout(flushSoon, wanted === 0 ? 0 : 350);
  }
  const flushSoon = () => { flush().catch(() => {}); };
  function flush() {
    flushTimer = 0;
    if (!pending.size) return Promise.resolve();
    return enqueue(async () => {
      let next = null;
      try {
        while (pending.size) { const updates = Object.fromEntries(pending); pending.clear(); next = await cartWrite('cart/update.js', { updates }); }
      } catch (error) { pending.clear(); lastRemoved = null; await recover(error); return; }
      if (!next) return;
      show(next); await settle(); announceUndo();
    });
  }
  // The change did not reach Shopify: show the real cart again (which puts back what was removed) and say why.
  async function recover(error) {
    try { await refresh(true); } catch {}
    status.textContent = error.message || root.dataset.errorLabel; q('retry').hidden = false; render();
  }
  function clearUndo() { clearTimeout(undoTimer); const el = q('undo'); if (el) { el.hidden = true; el.replaceChildren(); } }
  function announceUndo() {
    const el = q('undo'); const removed = lastRemoved; lastRemoved = null;
    if (!el || !removed) return;
    const text = document.createElement('span'); text.textContent = 'Removed ' + removed.title;
    const button = document.createElement('button'); button.type = 'button'; button.textContent = 'Undo';
    button.addEventListener('click', () => { clearUndo(); addLines([{ id: removed.id, quantity: removed.quantity, properties: removed.properties }]).catch(() => {}); });
    el.replaceChildren(text, button); el.hidden = false;
    undoTimer = setTimeout(clearUndo, 8000);
  }
  // ---- adding ----
  function reportAdd(items) {
    if (!session) return;
    items.forEach(item => {
      const line = session.lines.find(value => value.merchandiseId === gid(item.id));
      if (!line) return;
      const id = String(session.contentIdType === 'variant' ? line.merchandiseId : line.productId || line.merchandiseId).replace(/\D/g, '');
      try { window.fbq?.('track', 'AddToCart', { value: Number(line.unitPrice) * item.quantity, currency: session.currencyCode, content_type: 'product', content_ids: [id] }); window.gtag?.('event', 'add_to_cart', { currency: session.currencyCode, value: Number(line.unitPrice) * item.quantity, items: [{ item_id: id, quantity: item.quantity, price: Number(line.unitPrice) }] }); } catch {}
    });
  }
  // The cart as it will be after this add: new lines go to the top, an existing line (same book, same choices) just gets more copies.
  function predictLines(current, added) {
    const out = current.map(line => ({ ...line }));
    const fresh = [];
    added.forEach(item => {
      const merchandiseId = gid(item.id); const props = properties(item.properties);
      const wanted = { merchandiseId, quantity: 0, ...(Object.keys(props).length ? { properties: props } : {}) };
      const at = out.findIndex(line => line.merchandiseId === merchandiseId && canon([{ ...line, quantity: 0 }]) === canon([wanted]));
      if (at >= 0) out[at] = { ...out[at], quantity: out[at].quantity + item.quantity };
      else fresh.unshift({ ...wanted, quantity: item.quantity });
    });
    return [...fresh, ...out];
  }
  const currentLines = () => { try { return cart?.items ? cart.items.map(lineOf) : null; } catch { return null; } };
  async function addNow(normalized, checkout) {
    const version = ++viewVersion;
    retryCheckout = checkout; q('retry').hidden = true;
    // A cart that already exists has a stable token, so pricing can start now, alongside Shopify's add (it needs only the lines).
    const token = cookie('cart');
    const current = currentLines();
    const guess = token && current ? predictLines(current, normalized) : null;
    const early = guess && withinLimits(guess) ? priceRequest(guess, token) : null;
    if (early) early.catch(() => {});
    let added = false;
    try {
      const response = await cartWrite('cart/add.js', { items: normalized });
      added = true;
      if (!showAdded(response.items)) await refresh(false);
      await priceCurrent(early, guess ? signature(guess, token) : '');
      status.textContent = ''; render();
      verifySoon();
      if (checkout && version === viewVersion) launch();
      return true;
    } catch (error) {
      if (!added) { syncSession(); render(); status.textContent = error.message || root.dataset.errorLabel; q('retry').hidden = false; throw error; }
      session = null; render(); status.textContent = error.message || root.dataset.errorLabel; q('retry').hidden = false;
      return true;
    }
  }
  // Pricing did not wait for Shopify's cart read, so the read happens afterwards as a check (twice, as a cart read just after a write can be stale):
  // if another tab changed the cart, what is on screen is replaced with the real cart and priced again.
  function verifySoon() {
    setTimeout(() => enqueue(async () => {
      if (pending.size || flushTimer) return;
      const same = fresh => { try { return signature(fresh.items.map(lineOf), fresh.token) === signature((cart?.items || []).map(lineOf), cart?.token); } catch { return true; } };
      let fresh = await request(route('cart.js'));
      if (same(fresh)) return;
      await sleep(500);
      if (pending.size || flushTimer) return;
      fresh = await request(route('cart.js'));
      if (same(fresh)) return;
      cart = fresh; syncSession(); render(); await settle();
    }).catch(() => {}), 800);
  }
  async function addLines(items, { checkout = false, preview = null, button = null } = {}) {
    if (button?.dataset.tssPending) return false;
    const normalized = items.map(item => ({ id: gid(item.id ?? item.variantId).split('/').pop(), quantity: Number(item.quantity || 1), properties: properties(item.properties), ...(item.selling_plan ? { selling_plan: item.selling_plan } : {}) }));
    if (normalized.some(item => !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > MAX_QUANTITY)) throw new Error(root.dataset.tooManyLabel);
    // Shopify would accept more than ten of a book, but our checkout would not: say so before anything is added.
    const current = currentLines();
    if (current && !withinLimits(predictLines(current, normalized))) { open(); q('retry').hidden = true; status.textContent = root.dataset.tooManyLabel; throw new Error(root.dataset.tooManyLabel); }
    const restore = markPending(button, checkout ? 'Please wait…' : 'Adding…');
    // A book already in the cart only gets one more copy (no new row), so the pending row is only for a new line.
    const isNew = !normalized.every(item => cart?.items?.some(existing => String(existing.variant_id) === item.id));
    open(); q('retry').hidden = true; status.textContent = checkout ? root.dataset.loadingLabel : root.dataset.updatingLabel;
    if (isNew) showPendingRow(preview);
    if (pending.size) { clearTimeout(flushTimer); flushSoon(); }
    try {
      const success = await enqueue(() => addNow(normalized, checkout));
      if (success) reportAdd(normalized);
      return success;
    } finally { restore(); }
  }
  function formItem(form) {
    const data = new FormData(form);
    const values = {};
    for (const [name, value] of data) { const match = /^properties\[(.+)\]$/.exec(name); if (match && typeof value === 'string') values[match[1]] = value; }
    return { id: data.get('id'), quantity: Number(data.get('quantity') || 1), properties: values, selling_plan: data.get('selling_plan') || undefined };
  }
  window.TSSCheckout = { addLines, openCart: () => run(), checkout: () => run(true) };
  q('open').addEventListener('click', () => run());
  q('close').addEventListener('click', () => { viewVersion++; dialog.close(); restoreFocus?.focus?.(); });
  dialog.addEventListener('close', () => { document.documentElement.classList.remove('tss-cart-open'); if (shell.hidden) restoreFocus?.focus?.(); });
  dialog.addEventListener('cancel', () => { viewVersion++; });
  dialog.addEventListener('click', event => { if (event.target === dialog) { viewVersion++; dialog.close(); } });
  q('retry').addEventListener('click', () => run(retryCheckout));
  q('checkout-close').addEventListener('click', () => requestCheckoutExit());
  q('checkout-new-page').addEventListener('click', () => { if (session) location.assign(hostedUrl(session)); });
  window.addEventListener('popstate', () => { if (!shell.hidden && !history.state?.tssCheckout) requestCheckoutExit(false); });
  window.addEventListener('message', event => {
    if (event.origin !== apiOrigin || event.source !== frame.contentWindow || event.data?.source !== 'tss-checkout') return;
    if (event.data.type === 'ready') {
      checkoutReady = true;
      clearTimeout(shellTimer);
      q('checkout-loader').hidden = true;
      q('checkout-problem').hidden = true;
      frame.hidden = false;
    }
    if (event.data.type === 'exit-recovery-ready') exitRecoveryReady = event.data.enabled === true;
    if (event.data.type === 'exit-intercepted') { clearTimeout(exitRequestTimer); exitRequestTimer = null; }
    if (event.data.type === 'exit-continued' && exitBackPending) { exitBackPending = false; history.forward(); }
    if (event.data.type === 'close') closeCheckout(!exitBackPending);
    shell.dataset.gw = String(event.data.type === 'gateway');
    if (event.data.type === 'complete') consumePurchasedLines().catch(() => {});
  });
  async function consumePurchasedLines() {
    const latest = await request(route('cart.js'));
    const updates = {};
    purchasedLines.forEach(paid => {
      const item = latest.items.find(value => value.key === paid.key);
      if (item) updates[item.key] = Math.max(0, item.quantity - paid.quantity);
    });
    if (Object.keys(updates).length) await cartWrite('cart/update.js', { updates });
    purchasedLines = [];
    session = null; sessionSig = '';
    try { sessionStorage.removeItem('tss_native_checkout_lines_v1'); sessionStorage.removeItem(PRICED_KEY); localStorage.removeItem('tss_checkout_lines_v1'); } catch {}
    await refresh(false);
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, location.origin).pathname;
    if (/\/cart\/add(?:\.js)?\/?$/.test(action)) {
      event.preventDefault(); event.stopImmediatePropagation();
      addLines([formItem(form)], { preview: pagePreview(form), button: event.submitter || form.querySelector('button[type="submit"], button[name="add"]') }).catch(() => {});
    } else if (event.submitter?.name === 'checkout') { event.preventDefault(); event.stopImmediatePropagation(); run(true); }
  }, true);
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('button, a, input[type="submit"]');
    if (!target) return;
    if (target.matches('[data-tss-checkout-link], [name="checkout"], [data-tss-checkout-trigger]')) { event.preventDefault(); event.stopImmediatePropagation(); run(true); return; }
    if (target.matches('[data-tss-open-cart]')) { event.preventDefault(); run(); return; }
    if (target.matches('.shopify-payment-button__button, [data-shopify="payment-button"] button, [data-tss-buy], [data-tss-add]')) {
      const form = (target.closest('product-info, .product, section') || document).querySelector('form[action*="/cart/add"]');
      if (!form) return;
      event.preventDefault(); event.stopImmediatePropagation(); addLines([formItem(form)], { checkout: !target.matches('[data-tss-add]'), preview: pagePreview(form), button: target }).catch(() => {}); return;
    }
    if (target.tagName === 'A' && !root.contains(target)) {
      const url = new URL(target.href, location.origin);
      if (url.origin !== location.origin) return;
      if (/\/checkout\/?$/.test(url.pathname) || /\/cart\/checkout\/?$/.test(url.pathname)) { event.preventDefault(); event.stopImmediatePropagation(); run(true); }
      else if (/\/cart\/?$/.test(url.pathname)) { event.preventDefault(); event.stopImmediatePropagation(); run(); }
    }
  }, true);
  document.addEventListener('tss:add-variant', event => { if (event.detail?.variantId) addLines([{ id: event.detail.variantId, quantity: event.detail.quantity || 1, properties: event.detail.properties }], { checkout: Boolean(event.detail.redirect) }).catch(() => {}); });
  document.addEventListener('tss:checkout-cart', () => run(true));
  if (params.get('tss_done') === '1') {
    try { purchasedLines = JSON.parse(sessionStorage.getItem('tss_native_checkout_lines_v1') || '[]'); } catch {}
    const url = new URL(location.href); url.searchParams.delete('tss_done'); history.replaceState(history.state, '', url);
    consumePurchasedLines().catch(() => {});
  } else if (/\/cart\/?$/.test(location.pathname)) { document.body.classList.add('tss-cart-page'); run(); }
  else refresh(false).catch(() => {});
})();

